import React, { memo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "src/Icons";

interface SelectMenuItem {
  selected: boolean;
  Icon?: any;
  title: string;
  value: string;
  label: string;
}

const SelectMenuItem = ({
  value,
  selected,
  Icon,
  title,
  label,
  ...rest
}: SelectMenuItem) => {
  return (
    <MenuItem
      value={value}
      disableRipple
      {...rest}
      sx={{
        // borderBottomRadius: '2px',
        "&.MuiMenuItem-root": {
          minWidth: 120,
          fontSize: "14px",
          height: "42px",
          margin: "0",
          border: "none",
          backgroundColor: selected ? "#BF295C" : "transparent",
          color: selected ? "white" : "#1F1F1F",
          '&:hover:not([aria-selected="true"])': {
            backgroundColor: "#FAFAFA",
            color: "#BF295C",

            "& .Mui-checked": {
              backgroundColor: "transparent",
            },
          },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "transparent",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {Icon ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "4px",
            }}
          >
            <Icon fontSize="inherit" />
          </Box>
        ) : null}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
          }}
        >
          {title}
        </Typography>
      </Box>
    </MenuItem>
  );
};

interface DefaultSingleSelectorProps {
  value: any;
  placeholder?: string;
  valuesToSelect: Array<any>;
  onChanged: (value: string) => void;
  title: string;
  filterArray?: string[];
  containerStyle?: any;
  leftContainerStyle?: any;
  rightContainerStyle?: any;
  disabled?: boolean;
}

const DefaultSingleSelector = memo(({
  value,
  valuesToSelect,
  placeholder,
  onChanged,
  title,
  filterArray,
  containerStyle,
  leftContainerStyle,
  rightContainerStyle,
  disabled = false,
}: DefaultSingleSelectorProps) => {

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef<any>(null);

  const newValuesToSelect =
    filterArray
      ? valuesToSelect.filter(
        (service: { id: string }) => !filterArray.some((excluded) => excluded === service.id)
      )
      : valuesToSelect;

  const handleChange = ({ target: { value } }: any) => {
    onChanged?.(value.toString());
  };
  const toggleSelect = () => {
    if (!disabled) {
      setIsSelectOpen((state) => !state);
    }
  };

  const renderValue = (value: string) => {
    const { label, Icon } =
      valuesToSelect.find(({ id }: any) => +id == +value) || {};

    return value ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
        }}
      >
        {Icon ? (
          <Box
            sx={{
              color: "#1F1F1F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "4px",
            }}
          >
            <Icon fontSize="inherit" />
          </Box>
        ) : null}
        <Typography
          sx={{
            color: "#1F1F1F",
            fontSize: "14px",
          }}
        >
          {label}
        </Typography>
      </Box>
    ) : (
      <Typography
        sx={{
          color: "#676767",
          fontSize: "14px",
          fontWeight: "400",
        }}
      >
        {`Choose ${  placeholder}`}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        ...containerStyle,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "46px",
          minWidth: 170,
          paddingLeft: "16px",
          backgroundColor: "#F3F3F3",
          color: "#1F1F1F",
          border: "1px solid #D6D6DB",
          borderRight: "none",
          borderTopLeftRadius: "6px",
          borderBottomLeftRadius: "6px",
          ...leftContainerStyle,
        }}
      >
        <Typography
          sx={{
            fontWeight: "500",
            fontSize: "14px",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          border: "1px solid #D6D6DB",
          borderLeft: "none",
          borderTopRightRadius: "6px",
          borderBottomRightRadius: "6px",
          ...(isSelectOpen && {
            borderBottomRightRadius: "none",
          }),
          ...rightContainerStyle,
        }}
      >
        <FormControl sx={{ width: "100%" }}>
          <Select
            open={isSelectOpen}
            ref={selectRef}
            disabled={disabled}
            placeholder="Select input"
            value={value}
            onChange={handleChange}
            input={<OutlinedInput />}
            IconComponent={() => (
              <Box
                onClick={toggleSelect}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  height: "100%",
                  paddingRight: "8px",
                  color: "#828282",
                  cursor: disabled ? 'default' : "pointer",
                }}
              >
                {isSelectOpen ? (
                  <ChevronUp fontSize="inherit" />
                ) : (
                  <ChevronDown fontSize="inherit" />
                )}
              </Box>
            )}
            onOpen={() => setIsSelectOpen(true)}
            onClose={() => setIsSelectOpen(false)}
            renderValue={renderValue}
            displayEmpty
            sx={{
              borderRadius: "0px",
              color: "#828282",
              fontWeight: "400",
              height: "44px",
              boxShadow: "none",

              ...(isSelectOpen && {
                border: "none",
                borderRadius: 0,
                borderTopRightRadius: "2px",
                borderTopLeftRadius: "2px",
                boxShadow:
                  "rgba(49, 56, 116, 0.125) 0px 4px 4px 0px, rgba(49, 56, 116, 0.125) 0px 0px 4px 0px",
              }),

              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: 0,
                },

              "&.MuiOutlinedInput-root": {
                border: "none",
                fontSize: "14px",
                fontWeight: "400",

                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },

                "& .MuiOutlinedInput-input": {
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                  padding: "0px 16px",
                },
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 0,
                  boxShadow: "rgba(49, 56, 116, 0.25) 0px 4px 4px 0px",

                  "& .MuiList-root": {
                    padding: 0,
                  },
                },
              },
            }}
          >
            {newValuesToSelect.map(
              ({ id, Icon, label }: any) =>
                value !== id && (
                  <SelectMenuItem
                    key={id}
                    label={id}
                    value={id}
                    Icon={Icon}
                    selected={value === id}
                    title={label}
                  />
                )
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
});

export {DefaultSingleSelector}
